// Match exposure (Image weights shading): each photo's colour scaled so overlapping photos agree before they are
// blended (the camera's auto exposure and white balance change from frame to frame, so the weighted mean is blotchy
// otherwise). Each pair of neighbouring frames (2 apart at most, in id order) is compared where they see the same
// surface: one frame's points, placed in the other's depth camera, where its depth agrees. The median log ratio of
// their mean colours (colourGrid, photofilter.ts) there is one equation, log gain b - log gain a, per channel; least
// squares over all pairs gives each frame's gain (mean log gain 0). Pure.
import { type DepthFrame } from '../edit/weights.ts';
import { inverse } from '../align.ts';

/** A frame as Match exposure sees it: its depth image and camera, its photo's colour grid (gw × gh, RGB), its points (idx into P) and their pixels in it. */
export interface ExpoFrame { fr: DepthFrame; grid: Uint8Array; gw: number; gh: number; idx: number[]; pix: Int32Array }

/** One equation: log(colour b / colour a) per channel, from n points. */
export interface PairRatio { a: number; b: number; d: [number, number, number]; n: number }

const median = (v: number[]) => { v.sort((x, y) => x - y); return v[v.length >> 1]; };

/**
 * Median log ratio of frame b's colour to a's where both see a's points (P world positions, as the frames are placed):
 * a's points that land on a pixel of b's depth image within 5 % of b's depth there. Cells too dark or saturated
 * in either are left out. Null with fewer than minN such points. At most `cap` points looked at (spread over a's).
 */
export function pairRatio(P: Float32Array, a: ExpoFrame, b: ExpoFrame, minN = 30, cap = 3000): Omit<PairRatio, 'a' | 'b'> | null {
  const Pi = inverse(b.fr.place), R = Pi.R, t = Pi.t, K = b.fr.K, { dw, dh } = b.fr;
  const ch: number[][] = [[], [], []], stride = Math.max(1, Math.floor(a.idx.length / cap));
  const cell = (e: ExpoFrame, p: number) => {
    const x = p % e.fr.dw, y = (p / e.fr.dw) | 0;
    return 3 * (Math.min(e.gh - 1, Math.floor((y * e.gh) / e.fr.dh)) * e.gw + Math.min(e.gw - 1, Math.floor((x * e.gw) / e.fr.dw)));
  };
  for (let j = 0; j < a.idx.length; j += stride) {
    const pa = a.pix[j];
    if (pa < 0) continue;
    const i = a.idx[j], x = P[3 * i], y = P[3 * i + 1], z = P[3 * i + 2];
    const qz = R[6] * x + R[7] * y + R[8] * z + t[2];
    if (qz <= 1e-6) continue;
    const u = Math.round((K[0] * (R[0] * x + R[1] * y + R[2] * z + t[0])) / qz + K[2]), v = Math.round((K[1] * (R[3] * x + R[4] * y + R[5] * z + t[1])) / qz + K[3]);
    if (u < 0 || v < 0 || u >= dw || v >= dh) continue;
    const pb = v * dw + u, zb = b.fr.depth[pb];
    if (!(zb > 0) || Math.abs(zb - qz) > 0.05 * zb) continue;   // b sees something else there (in front, or behind)
    const ca = cell(a, pa), cb = cell(b, pb);
    let ok = true;
    for (let k = 0; k < 3; k++) { const p = a.grid[ca + k], q = b.grid[cb + k]; if (p < 8 || q < 8 || p > 250 || q > 250) ok = false; }
    if (!ok) continue;
    for (let k = 0; k < 3; k++) ch[k].push(Math.log(b.grid[cb + k] / a.grid[ca + k]));
  }
  const n = ch[0].length;
  if (n < minN) return null;
  return { d: [median(ch[0]), median(ch[1]), median(ch[2])], n };
}

/**
 * Each of n frames' colour gain (3 per frame, RGB: multiply its photo's colour by it) from the pairs' ratios.
 * With L a frame's log exposure, each pair says L_b - L_a = d (weight: its points / 100, at most 1); each frame's
 * L is also pulled to 0 (weight prior), since a pair's ratio is only good to about 0.1-0.2 and a chain of them
 * would wander (on the home scan by 0.7 over 120 frames): the gains correct frames against their neighbours,
 * not the whole scan's brightness. Least squares (Gauss-Seidel), the mean L of the frames in a pair taken out,
 * the gain exp(-L). A frame in no pair keeps gain 1. Gains kept within 0.5 to 2.
 */
export function solveGains(n: number, pairs: PairRatio[], prior = 0.5, iters = 200): Float32Array {
  // per frame: L_i = L_o + e (each equation as seen from either end)
  const L = new Float64Array(3 * n), adj: { o: number; e: number[]; w: number }[][] = Array.from({ length: n }, () => []);
  for (const p of pairs) {
    const w = Math.min(1, p.n / 100);
    adj[p.a].push({ o: p.b, e: p.d.map(v => -v), w });
    adj[p.b].push({ o: p.a, e: [...p.d], w });
  }
  const lam = Math.max(prior, 1e-3);   // (some pull always: it fixes the level, taken out below)
  for (let it = 0; it < iters; it++) for (let i = 0; i < n; i++) {
    const l = adj[i];
    if (!l.length) continue;
    for (let k = 0; k < 3; k++) {
      let s = 0, sw = lam;
      for (const q of l) { s += q.w * (L[3 * q.o + k] + q.e[k]); sw += q.w; }
      L[3 * i + k] = s / sw;
    }
  }
  const m = [0, 0, 0];
  let cnt = 0;
  for (let i = 0; i < n; i++) if (adj[i].length) { cnt++; for (let k = 0; k < 3; k++) m[k] += L[3 * i + k]; }
  const out = new Float32Array(3 * n).fill(1);
  for (let i = 0; i < n; i++) if (adj[i].length) for (let k = 0; k < 3; k++) out[3 * i + k] = Math.min(2, Math.max(0.5, Math.exp(m[k] / cnt - L[3 * i + k])));
  return out;
}

/** The pairs to compare among frames in id order: each with the next two. */
export const neighbourPairs = (n: number): [number, number][] => {
  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) for (let j = i + 1; j <= i + 2 && j < n; j++) out.push([i, j]);
  return out;
};
